// THE CAPABILITIES MANIFEST — which modules an INSTALL runs, and what each one
// contributes (ERP-DEPLOYMENT-MODEL §13, adopted 2026-09-20).
//
// An install declares its module set ONCE (`ENABLED_MODULES`, an install-time
// env var — the DEFAULT_LOCALE precedent), the gateway derives a manifest from
// it plus the modules' own contributions, and every consumer — routers, seams,
// portal nav, screens, the public site — asks the MANIFEST, never "is LIB
// installed?". Law D1 (ADR-002 §1) stays true: one artifact; the module set is
// configuration; branching on the DECLARED MODULE SET is the one sanctioned
// branch and hosted-vs-dedicated stays banned.
//
// WHY HERE and not in the gateway: the wire shape is parsed at BOTH ends (the
// gateway emits it, three portals and the public site read it), and each module
// contributes its own `<M>_CAPABILITIES` from its schemas lane — a lane that is
// zod-only and may reach `@erp/core-schemas` but nothing worker- or browser-
// bound. Used by every module → `packages/` (DEV-GUIDE-001 §3). Pure: zod and
// types, no I/O.
//
// A module never asks whether ANOTHER module is installed — it asks the
// manifest, or declares a slot the shell fills (the guard resolver seam, fin's
// charge sources). The module → module edge Fig 4.1 bans stays banned.
import { z } from "zod";

/**
 * Every module id, in CANONICAL ORDER. The manifest's `modules` and `features`
 * arrays are sorted by this tuple (module order, then declaration order), so
 * two installs with the same set emit byte-identical manifests and a consumer
 * may compare them as strings. Append-only: a new module goes at the end.
 */
export const MODULE_IDS = ["auth", "audit", "lib", "cms", "fin", "guard"] as const;
/** A module id: one of the six the repo knows. `ENABLED_MODULES` tokens parse through this. */
export const ModuleId = z.enum(MODULE_IDS);
export type ModuleId = z.infer<typeof ModuleId>;

/**
 * PLATFORM modules — always required, never optional. The gateway's middleware
 * chain writes DB_CORE/KV_SESSIONS (auth) and DB_AUDIT (audit) on EVERY request,
 * so an install that omits either could not serve a single one.
 */
export const PLATFORM_MODULES = ["auth", "audit"] as const satisfies readonly ModuleId[];

/**
 * The human label the 404 gate speaks ("The library module is not part of this
 * installation.") and the unavailable card renders. Lower-case on purpose: it
 * is dropped mid-sentence, and a screen that needs a heading capitalises it.
 */
export const MODULE_LABELS: Record<ModuleId, string> = {
  auth: "identity",
  audit: "audit trail",
  lib: "library",
  cms: "content",
  fin: "finance",
  guard: "gate desk",
};

/**
 * A feature id: `<module>.<name>`, lowercase kebab (`lib.opac`, `auth.step-up`).
 * Contributed by each module's `schemas/capabilities.ts`, fixed per release —
 * a feature is something a module SHIPS, not a runtime toggle.
 */
export const FeatureId = z
  .string()
  .regex(/^[a-z]+\.[a-z][a-z0-9-]*$/, "Use <module>.<name> in lowercase kebab, e.g. lib.opac");
export type FeatureId = z.infer<typeof FeatureId>;

/**
 * One module's contribution: its id and the features it ships. Every feature
 * must be namespaced under its own module — `modules/fin` cannot declare
 * `lib.opac`, and the refinement makes the attempt fail at import time (each
 * module parses its `<M>_CAPABILITIES` through this schema at load).
 */
export const ModuleCapabilities = z
  .object({ module: ModuleId, features: z.array(FeatureId) })
  .refine((c) => c.features.every((f) => f.startsWith(`${c.module}.`)), {
    message: "Every feature must be namespaced under its own module, e.g. lib.opac for module lib",
    path: ["features"],
  });
export type ModuleCapabilities = z.infer<typeof ModuleCapabilities>;

/**
 * The manifest as it crosses the wire — `GET /api/capabilities`, public,
 * `Cache-Control: public, max-age=60`. Both arrays are in canonical order.
 */
export const Capabilities = z.object({ modules: z.array(ModuleId), features: z.array(FeatureId) });
export type Capabilities = z.infer<typeof Capabilities>;

/** What `parseEnabledModules` answers: the canonical set, or one actionable reason. */
export type ParsedEnabledModules = { ok: true; modules: ModuleId[] } | { ok: false; reason: string };

const canonicalIndex = (id: ModuleId): number => MODULE_IDS.indexOf(id);
const sortCanonical = (ids: Iterable<ModuleId>): ModuleId[] =>
  [...ids].sort((a, b) => canonicalIndex(a) - canonicalIndex(b));

/**
 * Parse the `ENABLED_MODULES` env var: a comma list such as
 * `"auth,audit,lib,cms,fin,guard"`. Whitespace around a token is tolerated,
 * order is irrelevant (the result is canonical), an empty token (a trailing
 * comma) is ignored — but a DUPLICATE is refused, because a list someone typed
 * twice is a list someone edited without reading, and an UNKNOWN id is refused
 * rather than skipped, because `"lbi"` is a typo and not a module to leave out.
 * A missing platform module is refused: no request could be served without it.
 *
 * The reason names the offending token so the gateway's boot error is
 * actionable as-is; the gateway prefixes the var name.
 */
export function parseEnabledModules(raw: string | undefined): ParsedEnabledModules {
  const tokens = (raw ?? "")
    .split(",")
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
  if (tokens.length === 0) {
    return { ok: false, reason: `missing or empty — set it to a comma list such as "${MODULE_IDS.join(",")}"` };
  }
  const seen = new Set<ModuleId>();
  for (const token of tokens) {
    const parsed = ModuleId.safeParse(token);
    if (!parsed.success) {
      return { ok: false, reason: `unknown module id "${token}" — known ids are ${MODULE_IDS.join(", ")}` };
    }
    if (seen.has(parsed.data)) return { ok: false, reason: `duplicate module id "${token}"` };
    seen.add(parsed.data);
  }
  for (const platform of PLATFORM_MODULES) {
    if (!seen.has(platform)) {
      return {
        ok: false,
        reason: `platform module "${platform}" missing — ${PLATFORM_MODULES.join(" and ")} are always required`,
      };
    }
  }
  return { ok: true, modules: sortCanonical(seen) };
}

/**
 * Derive the manifest: the enabled modules in canonical order, and the
 * features of exactly those modules (module order, then each contribution's
 * declaration order). A contribution for a module that is NOT enabled is
 * simply not included — the gateway passes all six and the install decides.
 *
 * THROWS on a wiring error, loudly and at boot: an enabled module with no
 * contribution (its `<M>_CAPABILITIES` was never passed), a contribution whose
 * module is unknown or whose features are mis-namespaced (a hand-built object
 * that skipped the schema), or two contributions for one module.
 */
export function buildCapabilities(
  enabled: readonly ModuleId[],
  contributions: readonly ModuleCapabilities[],
): Capabilities {
  const byModule = new Map<ModuleId, ModuleCapabilities>();
  for (const contribution of contributions) {
    const module = ModuleId.safeParse(contribution.module);
    if (!module.success) {
      throw new Error(
        `capabilities: contribution for unknown module "${String(contribution.module)}" — known ids are ${MODULE_IDS.join(", ")}`,
      );
    }
    const parsed = ModuleCapabilities.safeParse(contribution);
    if (!parsed.success) {
      throw new Error(`capabilities: contribution for module "${module.data}" is malformed — ${parsed.error.message}`);
    }
    if (byModule.has(module.data)) {
      throw new Error(`capabilities: module "${module.data}" contributed twice`);
    }
    byModule.set(module.data, parsed.data);
  }
  const wanted = new Set<ModuleId>(enabled);
  const modules = MODULE_IDS.filter((id) => wanted.has(id));
  const features: FeatureId[] = [];
  for (const id of modules) {
    const contribution = byModule.get(id);
    if (!contribution) {
      throw new Error(`capabilities: module "${id}" is enabled but contributed no capabilities — pass its <M>_CAPABILITIES`);
    }
    features.push(...contribution.features);
  }
  return { modules, features };
}

/** Is this module part of the install? The question every seam asks in place of "is LIB installed?". */
export const hasModule = (caps: Pick<Capabilities, "modules">, id: ModuleId): boolean => caps.modules.includes(id);

/** Does the install ship this feature? A string, not `FeatureId`, so a screen may ask about a feature this build never heard of and get `false`. */
export const hasFeature = (caps: Pick<Capabilities, "features">, feature: string): boolean =>
  caps.features.includes(feature);
